import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { Security, CheckCircle, Warning } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

export default function TwoFactorDialog({ open, onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState('overview'); // 'overview' | 'setup' | 'verify' | 'disable'
  const [qrData, setQrData] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const has2fa = user?.has_2fa;

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/2fa/setup/');
      setQrData(res.data);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante la configurazione');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/2fa/confirm/', { otp_code: otpCode });
      setSuccess('2FA attivato con successo!');
      setStep('overview');
      // Update user data to reflect 2FA status
      if (user) user.has_2fa = true;
    } catch (err) {
      setError(err.response?.data?.error || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/2fa/disable/', { password });
      setSuccess('2FA disattivato');
      setStep('overview');
      setPassword('');
      if (user) user.has_2fa = false;
    } catch (err) {
      setError(err.response?.data?.error || 'Password non valida');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('overview');
    setQrData(null);
    setOtpCode('');
    setPassword('');
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Security color="primary" />
        Autenticazione a due fattori
      </DialogTitle>

      <Divider />

      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {step === 'overview' && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="body1">Stato 2FA:</Typography>
              {has2fa ? (
                <Chip icon={<CheckCircle />} label="Attivo" color="success" />
              ) : (
                <Chip icon={<Warning />} label="Non attivo" color="warning" />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              L'autenticazione a due fattori aggiunge un ulteriore livello di sicurezza
              al tuo account. Quando attivata, ti verrà chiesto un codice dal tuo
              telefono ogni volta che accedi.
            </Typography>

            <Typography variant="body2" color="text.secondary">
              App compatibili: Google Authenticator, Authy, Microsoft Authenticator
            </Typography>
          </Box>
        )}

        {step === 'verify' && qrData && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Scansiona questo QR code con la tua app di autenticazione:
            </Typography>

            <Box
              component="img"
              src={qrData.qr_code}
              alt="QR Code 2FA"
              sx={{
                width: 200,
                height: 200,
                mx: 'auto',
                my: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Oppure inserisci manualmente: <strong>{qrData.secret}</strong>
            </Typography>

            <TextField
              fullWidth
              label="Codice di verifica"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.3em' } }}
              sx={{ maxWidth: 300, mx: 'auto' }}
              helperText="Inserisci il codice a 6 cifre dalla tua app"
            />
          </Box>
        )}

        {step === 'disable' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disattivando il 2FA il tuo account sarà meno sicuro.
            </Alert>
            <TextField
              fullWidth
              label="Password attuale"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Conferma con la tua password per disattivare il 2FA"
            />
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Chiudi</Button>

        {step === 'overview' && !has2fa && (
          <Button variant="contained" onClick={handleSetup} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Attiva 2FA'}
          </Button>
        )}

        {step === 'overview' && has2fa && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setStep('disable')}
          >
            Disattiva 2FA
          </Button>
        )}

        {step === 'verify' && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || otpCode.length < 6}
          >
            {loading ? <CircularProgress size={20} /> : 'Conferma'}
          </Button>
        )}

        {step === 'disable' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable}
            disabled={loading || !password}
          >
            {loading ? <CircularProgress size={20} /> : 'Disattiva'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
